import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { EMPTY, merge, Observable, of, ReplaySubject } from 'rxjs';
import { FormHelper } from '@helpers/form.helper';
import { enumEntityDataSource } from '@data-layer/enums/isf';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { enumEntityByKey, enumEntityModelCode } from '@data-layer/enums/entities';
import { EntityModel } from '@data-layer/models/entities';
import { CompanyModel } from '@data-layer/models/companies';
import { CompaniesService } from '@services/companies.service';
import { IsfPossibleEntitiesService, IsfStateService } from '@shared/isf-mutual/services';
import { IsfModel, IsfShortModel } from '@data-layer/models/isf';
import { ValidationMessageModel } from '@data-layer/models/common';
import { enumIdentifierQualifier, enumPlatformType } from '@data-layer/enums/common';
import { isImporterFromCompany } from '@helpers/importer.helper';

@Component({
  selector: 'entity-unify-default-desktop',
  template: `
    <p class="sidebar-form-description">{{ info?.subTitle | translate }}</p>

    <span class="sidebar-form-hint" *ngIf="possibleIsfEntities?.length">{{ 'LABELS.USE_PREV_INFO' | translate }}</span>

    <form [formGroup]="formGroup" autocomplete="off" (ngSubmit)="continue()">
      <ng-container *ngIf="isfEntityCode === enumEntityModelCode.IMPORTER">
        <entity-unify-card
          *ngIf="company"
          [isfEntity]="companyImporter"
          [formGroup]="formGroup"
          [code]="enumEntityModelCode.IMPORTER"
          [isDesktop]="true"
          (areaClicked)="setCustomFormActive(false)"
        ></entity-unify-card>
      </ng-container>

      <ng-container *ngFor="let isfEntity of possibleIsfEntities; let inx = index">
        <entity-unify-card
          [isfEntity]="isfEntity"
          [formGroup]="formGroup"
          [code]="isfEntityCode"
          [isDesktop]="true"
          [index]="cardLabelIndex(isfEntity)"
          (areaClicked)="setCustomFormActive(false)"
        ></entity-unify-card>
      </ng-container>

      <span
        class="sidebar-form-hint mt-3"
        *ngIf="possibleIsfEntities?.length || (isfEntityCode === enumEntityModelCode.IMPORTER && !isImporterFromCompany)"
      >
        {{ 'LABELS.OR_ADD_DETAILS' | translate }}
      </span>

      <entity-unify-update
        [isfEntity]="updateIsfEntity"
        [formGroup]="formGroup"
        [code]="isfEntityCode"
        [errors]="errors"
        [isDesktop]="true"
        [isClearCountry]="isClearCountry"
        (areaClicked)="setCustomFormActive(true)"
        (formResetted)="resetFormIds()"
      ></entity-unify-update>

      <div class="sidebar-form-buttons">
        <button type="submit" class="btn btn-primary">{{ 'BUTTON.SAVE' | translate }}</button>
      </div>
    </form>

    <missing-fields [code]="'LABELS.ISF'" [canDecline]="true" (confirm)="confirm()" (decline)="decline()"></missing-fields>
  `,
  styleUrls: ['./entity-unify-default-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityUnifyDefaultDesktopComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public info: {
    code: enumEntityModelCode;
    CODE: enumEntityModelCode; // TODO check & refactor later
    imgSrc: string;
    titleDesktop: string;
    subTitle: string;
    hint: string;
    addDetails: string;
    tooltip: string;
    addNew: string;
    subTitleHint: string;
  } = null;
  @Input() public entityId: number;
  @Input() public invoiceId: number;
  @Output() public dataSaved: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;
  public isfEntityCode: enumEntityModelCode;

  public company: CompanyModel;

  public formGroup: UntypedFormGroup;
  public errors = this.fh.createErrorsForIsfFormGroup();

  public updateIsfEntity: EntityModel;
  public currentIsfEntity: EntityModel;
  public possibleIsfEntities: EntityModel[] = [];
  public companyImporter: EntityModel;

  public isValid: boolean;
  public isCustomFormActive: boolean;

  public COMPANY_IMPORTER_ID = 1;

  public enumEntityModelCode = enumEntityModelCode;

  private addPart = {};
  private isClearClicked: boolean;
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private isfStateService: IsfStateService,
    private fh: FormHelper,
    private cd: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private companiesService: CompaniesService,
    private isfPossibleEntitiesService: IsfPossibleEntitiesService,
    private activeRoute: ActivatedRoute,
  ) {}

  public ngAfterViewInit() {
    if (!this.fh.isValidIsfEntity(this.currentIsfEntity)) {
      this.fh.setEntityBackError(this.formGroup, this.currentIsfEntity, this.errors);
    }

    // used for MF form when inherited from supplier
    const seller = this.isfStateService.getIsfEntityById(enumEntityByKey.SELLER);
    if (this.isfEntityCode === enumEntityModelCode.MANUFACTURER && !!seller && this.entityId === seller.id) {
      this.possibleIsfEntities.push(seller);
    }

    this.cd.detectChanges();
  }

  public ngOnInit(): void {
    this.isfEntityCode = this.info?.code || this.info?.CODE;

    this.formGroup = this.fh.createIsfEntityFormGroup(this.isfEntityCode, this.destroy$);
    if (!this.isfStateService.currentIsf) {
      this.updateCreateEntity(this.isfStateService.initImporterFromCompany())
        .pipe(
          switchMap((entity) => this.createOfGetIsf({ importerId: entity.id })),
          takeUntil(this.destroy$),
        )
        .subscribe((isf) => {
          this.isfStateService.currentIsf = isf;
          this.possibleIsfEntities = this.isfPossibleEntitiesService.calculatePossibleIsfEntities(
            this.isfEntityCode,
            this.possibleIsfEntities,
          );

          this.cd.detectChanges();
        });
    } else {
      this.possibleIsfEntities = this.isfPossibleEntitiesService.calculatePossibleIsfEntities(this.isfEntityCode, this.possibleIsfEntities);
    }
    this.company = this.companiesService.getCompany();

    this.addPart = {
      entityCode: this.isfEntityCode,
      entityIdentifierQualifier: null, // TODO check this -> enumIdentifierQualifier.EIN,
      type:
        [enumEntityModelCode.CONSIGNEE, enumEntityModelCode.BUYER, enumEntityModelCode.SHIP_TO].indexOf(this.isfEntityCode) !== -1 ? 1 : 2,
    };

    this.checkValidState();
    this.updateCurrentIsfEntity();

    this.fh.patchIsfEntity(this.formGroup, this.currentIsfEntity);

    if (!this.formGroup.value.dataSource) {
      const st = this.possibleIsfEntities?.[0];
      if (this.formGroup.value.id) {
        this.formGroup.patchValue({ dataSource: this.currentIsfEntity.id, code: this.currentIsfEntity.entityCode });
      } else {
        // discussed with Inna
        // this.formGroup.patchValue({ ...st, dataSource: st?.id, code: st?.entityCode });
      }
    }

    this.companyImporter = this.isfStateService.initImporterFromCompany(this.COMPANY_IMPORTER_ID);
    if (this.isImporterFromCompany && this.isfEntityCode === enumEntityModelCode.IMPORTER) {
      this.formGroup.get('dataSource').setValue(this.COMPANY_IMPORTER_ID);
    }
    // #9774
    if (!this.entityId && !this.formGroup.get('entityIdentifierQualifier').value && this.isfEntityCode === enumEntityModelCode.CONSIGNEE) {
      this.formGroup.get('entityIdentifierQualifier').setValue(enumIdentifierQualifier.EIN);
    }

    merge(
      this.formGroup.get('entityIdentifier').valueChanges,
      this.formGroup.get('entityIdentifierQualifier').valueChanges,
      this.formGroup.get('name').valueChanges,
      this.formGroup.get('dateOfBirth').valueChanges,
      this.formGroup.get('address').valueChanges,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setFormIds();
        this.isClearClicked = false;
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public continue(): void {
    if (this.formGroup.invalid) {
      const invalidFields = this.fh.validateAllFields(this.formGroup);
      if (Object.keys(invalidFields).length) {
        this.openModal(invalidFields);
        return;
      }
    }

    this.saveEntity();
  }

  public skip(): void {
    if (!this.currentIsfEntity.id) {
      return;
    }

    this.isfStateService
      .deleteIsfEntity(this.currentIsfEntity.id)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  public confirm(): void {}

  public decline(): void {
    this.saveEntity();
  }

  public checkValidState(): void {
    this.isValid = this.formGroup.valid;

    this.formGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.formGroup.value.dataSource !== enumEntityDataSource.NEW) {
        this.isValid = true;
        return;
      }
      const isValidForm = this.formGroup.get('entityIdentifier').valid && this.formGroup.get('entityIdentifierQualifier').valid;
      if (!isValidForm) {
        this.isValid = isValidForm;
        return;
      }

      this.isValid = !this.fh.isEmptyEntityForm(this.formGroup);
    });
  }

  public cardLabelIndex(isfEntity: EntityModel): number {
    if (isfEntity.entityCode !== enumEntityModelCode.MANUFACTURER) {
      return;
    }

    return (
      this.possibleIsfEntities.findIndex((item) => item.id === isfEntity.id && item.entityCode === enumEntityModelCode.MANUFACTURER) || 0
    );
  }

  public setCustomFormActive(val: boolean): void {
    // used for #5311
    // to have info when we use custom form & when existing entity
    this.isCustomFormActive = val;
  }

  public get isImporterFromCompany(): boolean {
    return isImporterFromCompany(this.currentIsfEntity, this.company);
  }

  public get isClearCountry(): boolean {
    return ![enumEntityModelCode.IMPORTER, enumEntityModelCode.CONSIGNEE, enumEntityModelCode.BUYER, enumEntityModelCode.SHIP_TO].includes(
      this.updateIsfEntity?.entityCode,
    );
  }

  public setFormIds(): void {
    if (
      this.isCustomFormActive &&
      (!this.currentIsfEntity.id ||
        this.possibleIsfEntities.find((item) => item.id === this.currentIsfEntity.id) ||
        this.currentIsfEntity.entityCode === enumEntityModelCode.IMPORTER)
    ) {
      this.formGroup.patchValue({
        id: null,
        dataSource: null,
        code: this.isfEntityCode,
      });

      // #6214 prevent UnprocessableEntityException error
      if ([enumEntityModelCode.SHIP_TO].includes(this.isfEntityCode)) {
        this.formGroup.patchValue(
          {
            entityIdentifierQualifier: null,
            entityIdentifier: null,
          },
          { emitEvent: false },
        );
      }
    }
  }

  public resetFormIds(): void {
    this.formGroup.patchValue({ id: null, dataSource: null, entityCode: this.isfEntityCode });
    this.isClearClicked = true;
  }

  private saveEntity() {
    const isfEntityForUpdate = this.prepareToUpdate();

    // TODO: FIX SET ADDRESS_ID
    if (this.fh.isEmptyFormAll(this.formGroup.get('address') as UntypedFormGroup)) {
      isfEntityForUpdate.address = null;
      isfEntityForUpdate.addressId = null;
    }

    if (isfEntityForUpdate.address && !isfEntityForUpdate.address.id) {
      isfEntityForUpdate.address.id = 0;
    }

    if (isfEntityForUpdate.address) {
      isfEntityForUpdate.addressId = isfEntityForUpdate.address.id;
    }

    const keyId = enumEntityByKey[this.getEntityKeyByValue(this.isfEntityCode)];

    if (
      isfEntityForUpdate.id === this.COMPANY_IMPORTER_ID ||
      (this.isfEntityCode === enumEntityModelCode.IMPORTER && !isfEntityForUpdate.id)
    ) {
      isfEntityForUpdate.id = this.isfStateService.currentIsf?.importer?.id;
    }

    this.updateCreateEntity(isfEntityForUpdate)
      .pipe(
        switchMap((res) => {
          if (this.isfEntityCode === enumEntityModelCode.IMPORTER) {
            this.isfStateService.currentIsf = { ...this.isfStateService.currentIsf, importer: res, importerId: res?.id || null };
            return this.isfStateService.updateIsf({ ...this.isfStateService.currentIsf, importer: res, importerId: res?.id || null });
          } else {
            // manufacturer uses invoices model, update invoice, for other entities -> shipment or isf
            if (this.isfEntityCode === enumEntityModelCode.MANUFACTURER) {
              // if manufacturer exists - update only entity, don't push it again
              if (this.invoiceId) {
                const invoice = this.isfStateService.currentIsf?.shipment?.invoices?.find(
                  (invoiceItem) => invoiceItem.id === this.invoiceId,
                );

                return this.isfStateService.updateManufacturer(invoice, res?.id || null);
              } else {
                return this.isfStateService.pushManufacturer(res?.id || null);
              }
            } else {
              return this.isfStateService.updateShipment({
                ...this.isfStateService.currentIsf.shipment,
                [keyId]: res?.id || null,
              });
            }
          }
        }),
        switchMap(() => this.isfStateService.getSummaryById(this.isfStateService.currentIsf.referenceNumber)),
        takeUntil(this.destroy$),
      )
      .subscribe((updatedIsf) => {
        // due to validations we have to reload the whole isf object
        this.isfStateService.currentIsf = updatedIsf;
        this.dataSaved.emit();
      });
  }

  private openModal(data: [{ [key: string]: ValidationMessageModel }]) {
    this.missingFieldModal.openCustomModal(data);
  }

  private updateCurrentIsfEntity() {
    // get default entities by code, manufacturer - by id from invoices list
    let isfEntity: EntityModel;
    if (this.isfEntityCode === enumEntityModelCode.MANUFACTURER && this.entityId) {
      isfEntity = this.isfStateService.getIsfManufacturerEntityList().find((item) => item.id === this.entityId);
    } else {
      isfEntity = this.isfStateService.getIsfEntityById(enumEntityByKey[this.getEntityKeyByValue(this.isfEntityCode)]);
    }

    this.currentIsfEntity = !isfEntity
      ? new EntityModel({
          ...this.addPart,
        })
      : new EntityModel(isfEntity);

    this.updateIsfEntity =
      isfEntity && !this.possibleIsfEntities.find((item) => item.id === isfEntity.id)
        ? new EntityModel({ ...this.currentIsfEntity, ...this.addPart })
        : new EntityModel({ ...this.addPart });
  }

  private prepareToUpdate(): EntityModel {
    const isfEntity = new EntityModel({
      ...this.formGroup.value,
      entityCode: this.isfEntityCode,
    });

    return isfEntity;
  }

  private updateCreateEntity(entity: EntityModel): Observable<EntityModel> {
    if (this.isClearClicked) {
      return of(null);
    }

    if (entity.id && this.possibleIsfEntities.map((item) => item.id).includes(entity.id)) {
      return of(entity);
    } else {
      return this.isfStateService.updateEntity(entity);
    }
  }

  private getEntityKeyByValue(code: string): string {
    return Object.keys(enumEntityModelCode).find((key) => enumEntityModelCode[key] === code);
  }

  private createOfGetIsf(params?: Partial<IsfShortModel>): Observable<IsfModel> {
    if (!this.isfStateService.currentIsf) {
      return this.isfStateService.createIsf({ ...params, platformType: enumPlatformType.DESKTOP }).pipe(
        tap((isf: IsfModel) => {
          this.isfStateService.currentIsf = isf;
        }),
      );
    }

    return of(this.isfStateService.currentIsf);
  }
}
